import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { adminService, dataService } from '../../../services';
import DatasetRecordsModal from '../../../components/DatasetRecordsModal';
import type { Dataset, CreateDatasetRequest } from '../../../types';

const DatasetsPage: React.FC = () => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  // 记录查看模态框状态
  const [recordsModalVisible, setRecordsModalVisible] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<{ id: number; name: string } | null>(null);

  const fetchDatasets = async () => {
    try {
      setLoading(true);
      const response = await adminService.getDatasets();
      setDatasets(response.datasets);
    } catch (error) {
      console.error('获取数据集列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasets();
  }, []);

  const handleCreate = async (values: CreateDatasetRequest) => {
    try {
      const response = await adminService.createDataset(values);
      message.success(response.message);
      setModalVisible(false);
      form.resetFields();
      fetchDatasets();
    } catch (error) {
      console.error('创建数据集失败:', error);
    }
  };

  const handleDelete = async (datasetId: number, name: string) => {
    try {
      const response = await adminService.deleteDataset(datasetId);
      message.success(response.message);
      fetchDatasets();
    } catch (error) {
      console.error('删除数据集失败:', error);
    }
  };

  const handleExport = async (datasetId: number, name: string) => {
    try {
      const blob = await dataService.exportDataset(datasetId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}_数据导出.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
    }
  };

  const handleViewRecords = (datasetId: number, name: string) => {
    setSelectedDataset({ id: datasetId, name });
    setRecordsModalVisible(true);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left' as const,
    },
    {
      title: '数据集名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: '创建者',
      dataIndex: 'creator',
      key: 'creator',
      width: 100,
    },
    {
      title: '记录数量',
      dataIndex: 'phone_count',
      key: 'phone_count',
      width: 100,
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right' as const,
      render: (_: any, record: Dataset) => (
        <Space size="small" direction="vertical" style={{ width: '100%' }}>
          <Space size="small" wrap>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewRecords(record.id, record.name)}
            >
              查看
            </Button>
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleExport(record.id, record.name)}
            >
              导出
            </Button>
            <Popconfirm
              title={`确定要删除数据集 ${record.name} 吗？`}
              onConfirm={() => handleDelete(record.id, record.name)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="数据集管理"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            创建数据集
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={datasets}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="创建数据集"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            label="数据集名称"
            name="name"
            rules={[{ required: true, message: '请输入数据集名称!' }]}
          >
            <Input placeholder="请输入数据集名称" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
          >
            <Input.TextArea placeholder="请输入描述" rows={4} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                创建
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 记录查看模态框 */}
      {selectedDataset && (
        <DatasetRecordsModal
          visible={recordsModalVisible}
          datasetId={selectedDataset.id}
          datasetName={selectedDataset.name}
          onClose={() => {
            setRecordsModalVisible(false);
            setSelectedDataset(null);
          }}
        />
      )}
    </div>
  );
};

export default DatasetsPage;
